import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash, Drop, ArrowsClockwise, NotePencil, Warning, Toilet, ListChecks, Clock, ForkKnife, Footprints, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { IncidentReportDialog } from "../components/IncidentReportDialog";
import { GuidelinesNotice } from "../components/GuidelinesNotice";
import { PlanSignoffNotice } from "../components/PlanSignoffNotice";

const TYPE_DEFS = [
  { key: "note", label: "Note", icon: NotePencil, module: null },
  { key: "fall", label: "Fall / Near-miss", icon: Warning, module: "fall_risk" },
  { key: "intake", label: "Intake", icon: Drop, module: "intake" },
  { key: "voiding", label: "Voiding", icon: Toilet, module: "voiding" },
  { key: "bowel", label: "Bowel", icon: ListChecks, module: "bowel" },
  { key: "turning", label: "Turning", icon: ArrowsClockwise, module: "turning" },
  { key: "careclock", label: "Care Clock", icon: Clock, module: "care_clock" },
  { key: "meal", label: "Meal", icon: ForkKnife, module: "meals" },
  { key: "wandering", label: "Wandering", icon: Footprints, module: "wandering" },
  { key: "supervision", label: "Supervision", icon: Eye, module: "supervision" },
];

const SELECTS = {
  fallWitnessed: ["Yes", "No", "Unsure"],
  fallLocation: ["Bathroom", "Bedroom", "Kitchen", "Living room", "Hallway", "Stairs", "Outdoors", "Other"],
  fallInjury: ["None apparent", "Minor — handled at home", "ER or urgent care"],
  voidOutcome: ["Voided", "Attempted — no void", "Refused"],
  voidAmount: ["", "Small", "Medium", "Large"],
  bowelOccurred: ["Yes", "No"],
  turnPosition: ["Left side", "Right side", "Back", "Prone", "Other"],
  clockStatus: ["Done", "Done late", "Missed"],
  mealName: ["Breakfast", "Lunch", "Dinner", "Snack"],
  mealPct: ["100%", "75%", "50%", "25%", "Refused"],
  wandRedirected: ["Yes", "No"],
  supObservation: ["Supervised throughout", "Left alone briefly", "Check-in completed", "Exit-seeking observed"],
};

const Sel = ({ value, onChange, options, tid, className = "" }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className={`h-9 rounded-md border border-stone-300 bg-white px-3 text-sm ${className}`} data-testid={tid}>
    {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
  </select>
);

export default function ShiftLogPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [notes, setNotes] = useState([]);
  const [shiftDate, setShiftDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [logType, setLogType] = useState("note");
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const [incidentPrompt, setIncidentPrompt] = useState(null);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState(null);
  const [skipReason, setSkipReason] = useState("");

  const setF = (k, v) => setFields((s) => ({ ...s, [k]: v }));

  const load = () => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch((e) => { toast.error(formatApiError(e)); navigate("/"); });
    api.get(`/clients/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {});
    api.get("/modules").then((r) => setRegistry(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const activeModules = new Set(
    Object.entries(client?.modules || {}).filter(([, v]) => v?.active).map(([k]) => k)
  );
  const visibleTypes = TYPE_DEFS.filter((t) => !t.module || activeModules.has(t.module));

  const buildDetails = () => {
    switch (logType) {
      case "fall": return { witnessed: fields.fallWitnessed || "Unsure", location: fields.fallLocation || "Other", injury: fields.fallInjury || "None apparent" };
      case "intake": return { amount_ml: (fields.intakeAmount || "").trim(), fluid: (fields.intakeFluid || "").trim() };
      case "voiding": return { outcome: fields.voidOutcome || "Voided", amount: fields.voidAmount || "" };
      case "bowel": return { occurred: fields.bowelOccurred || "Yes" };
      case "turning": return { position: fields.turnPosition || "Left side" };
      case "careclock": return { task: (fields.clockTask || "").trim(), check_time: fields.clockTime || "", status: fields.clockStatus || "Done" };
      case "meal": return { meal: fields.mealName || "Breakfast", intake_pct: fields.mealPct || "100%" };
      case "wandering": return { redirected: fields.wandRedirected || "Yes", location: (fields.wandLocation || "").trim() };
      case "supervision": return { observation: fields.supObservation || "Supervised throughout" };
      default: return null;
    }
  };

  const add = async () => {
    const details = logType === "note" ? null : buildDetails();
    if (logType === "note" && !note.trim()) { toast.error("Note text required"); return; }
    if (logType === "intake" && !details.amount_ml) { toast.error("Enter an amount (mL)"); return; }
    if (logType === "careclock" && !details.task) { toast.error("Enter the task or check"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${id}/notes`, { shift_date: shiftDate, note: note.trim(), log_type: logType, details });
      if (logType === "fall" && details.injury !== "None apparent") {
        setIncidentPrompt({
          description: `Fall on ${shiftDate}. Witnessed: ${details.witnessed}. Location: ${details.location}. Injury: ${details.injury}.${note.trim() ? " " + note.trim() : ""}`,
        });
      }
      setNote("");
      setFields({});
      load();
      toast.success("Log entry added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (nid) => {
    try {
      await api.delete(`/clients/${id}/notes/${nid}`);
      setNotes((ns) => ns.filter((n) => n.id !== nid));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const skip = async () => {
    if (!skipTarget) return;
    setBusy(true);
    try {
      await api.post(`/clients/${id}/notes`, {
        shift_date: new Date().toISOString().slice(0, 10),
        note: "",
        log_type: skipTarget.logType,
        details: { skipped: true, item: skipTarget.text, reason: skipReason.trim() },
      });
      setSkipTarget(null);
      setSkipReason("");
      load();
      toast.success("Skip recorded — deliberate, not forgotten");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const typeMeta = (t) => {
    if (t === "fluid") return { key: "fluid", label: "Intake", icon: Drop, module: "intake" };
    return TYPE_DEFS.find((x) => x.key === (t || "note")) || TYPE_DEFS[0];
  };
  const describe = (n) => {
    const d = n.details;
    if (!d) return null;
    if (d.skipped) return `Skipped — ${d.item || "documentation item"}${d.reason ? ` (${d.reason})` : ""}`;
    switch (n.log_type) {
      case "fall": return `Fall — ${d.location || "location unknown"} · Witnessed: ${d.witnessed || "?"} · Injury: ${d.injury || "?"}`;
      case "fluid":
      case "intake": return `Intake${d.amount_ml ? ` — ${d.amount_ml} mL` : ""}${d.fluid ? ` (${d.fluid})` : ""}`;
      case "voiding": return `Voiding — ${d.outcome || "noted"}${d.amount ? ` (${d.amount})` : ""}`;
      case "bowel": return `Bowel movement — ${d.occurred || "noted"}`;
      case "turning": return `Repositioned — ${d.position || "position noted"}`;
      case "careclock": return `${d.task || "Check"}${d.check_time ? ` at ${d.check_time}` : ""} — ${d.status || "Done"}`;
      case "meal": return `${d.meal || "Meal"} — ate ${d.intake_pct || "?"}`;
      case "wandering": return `Exit-seeking observed${d.location ? ` (${d.location})` : ""} — redirected: ${d.redirected || "?"}`;
      case "supervision": return `Supervision — ${d.observation || "noted"}`;
      default: return null;
    }
  };

  if (!client) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const activeModuleDefs = registry.filter((m) => activeModules.has(m.key));

  return (
    <div className="max-w-3xl" data-testid="shift-log-page">
      <GuidelinesNotice client={client} />
      <PlanSignoffNotice client={client} />
      <button onClick={() => navigate(`/clients/${id}`)} className="mb-4 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="back-to-client">
        <ArrowLeft size={14} /> Back to client
      </button>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Shift &amp; Care Log</h1>
        <span className="rounded-lg border border-stone-200 bg-accent px-3 py-1 text-sm font-semibold text-accent-foreground">{client.forms?.intake?.full_name || client.client_code}</span>
      </div>

      {activeModuleDefs.length > 0 && (
        <div className="mb-6 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="active-modules-panel">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-stone-500">Active documentation modules</p>
          <div className="space-y-3">
            {activeModuleDefs.map((m) => {
              const toggles = (client.modules || {})[m.key]?.questions || {};
              const enabled = (m.questions || []).filter((q) => toggles[q.id] !== false);
              return (
                <div key={m.key} data-testid={`active-module-${m.key}`}>
                  <p className="text-sm font-medium text-stone-900">{m.name}</p>
                  {enabled.length > 0 && (
                    <ul className="mt-1 space-y-1 text-xs text-stone-500">
                      {enabled.map((q) => (
                        <li key={q.id} className="flex items-center justify-between gap-2" data-testid={`panel-item-${q.id}`}>
                          <span className="flex items-start gap-1.5"><span className="mt-0.5">•</span>{q.text}</span>
                          {skipTarget?.qid === q.id ? (
                            <span className="flex shrink-0 items-center gap-1">
                              <input value={skipReason} onChange={(e) => setSkipReason(e.target.value)} placeholder="Reason (optional)"
                                className="h-6 w-32 rounded border border-stone-300 bg-white px-2 text-[11px]" data-testid={`skip-reason-${q.id}`} />
                              <button onClick={skip} disabled={busy} className="text-[11px] font-semibold text-[#C0392B] hover:underline" data-testid={`skip-confirm-${q.id}`}>Confirm</button>
                              <button onClick={() => { setSkipTarget(null); setSkipReason(""); }} className="text-[11px] text-stone-400 hover:underline">Cancel</button>
                            </span>
                          ) : (
                            <button onClick={() => { setSkipTarget({ qid: q.id, text: q.text, logType: m.log_type }); setSkipReason(""); }}
                              className="shrink-0 text-[11px] font-medium text-stone-400 hover:text-[#C0392B]" data-testid={`skip-${q.id}`}>
                              Skip
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-3 flex flex-wrap gap-2" data-testid="log-type-picker">
          {visibleTypes.map((t) => {
            const Icon = t.icon;
            const active = logType === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setLogType(t.key)}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-500 hover:border-stone-400"}`}
                data-testid={`log-type-${t.key}`}>
                <Icon size={14} weight={active ? "fill" : "regular"} /> {t.label}
              </button>
            );
          })}
        </div>
        {visibleTypes.length === 1 && (
          <p className="mb-3 rounded-md bg-accent/60 px-3 py-2 text-xs text-stone-500" data-testid="log-modules-hint">
            Tip: activate documentation modules (Modules button on the client page) to unlock typed care logs — intake, turning, falls, and more.
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[180px_1fr]">
          <Input type="date" value={shiftDate} onChange={(e) => setShiftDate(e.target.value)} className="border-stone-300 bg-white" data-testid="note-date" />
          {logType === "note" && (
            <Textarea rows={2} placeholder="What happened on this shift? Anything the next caregiver or the office should know?" value={note} onChange={(e) => setNote(e.target.value)}
              className="border-stone-300 bg-white" data-testid="note-text" />
          )}
          {logType === "fall" && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Sel value={fields.fallWitnessed || "Unsure"} onChange={(v) => setF("fallWitnessed", v)} options={SELECTS.fallWitnessed} tid="fall-witnessed" />
              <Sel value={fields.fallLocation || "Bathroom"} onChange={(v) => setF("fallLocation", v)} options={SELECTS.fallLocation} tid="fall-location" />
              <Sel value={fields.fallInjury || "None apparent"} onChange={(v) => setF("fallInjury", v)} options={SELECTS.fallInjury} tid="fall-injury" />
            </div>
          )}
          {logType === "intake" && (
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" min="0" placeholder="Amount (mL)" value={fields.intakeAmount || ""} onChange={(e) => setF("intakeAmount", e.target.value)} className="border-stone-300 bg-white" data-testid="intake-amount" />
              <Input placeholder="Fluid (e.g. water, juice)" value={fields.intakeFluid || ""} onChange={(e) => setF("intakeFluid", e.target.value)} className="border-stone-300 bg-white" data-testid="intake-fluid" />
            </div>
          )}
          {logType === "voiding" && (
            <div className="grid grid-cols-2 gap-3">
              <Sel value={fields.voidOutcome || "Voided"} onChange={(v) => setF("voidOutcome", v)} options={SELECTS.voidOutcome} tid="voiding-outcome" />
              <Sel value={fields.voidAmount || ""} onChange={(v) => setF("voidAmount", v)} options={SELECTS.voidAmount} tid="voiding-amount" />
            </div>
          )}
          {logType === "bowel" && (
            <Sel value={fields.bowelOccurred || "Yes"} onChange={(v) => setF("bowelOccurred", v)} options={SELECTS.bowelOccurred} tid="bowel-occurred" className="md:w-56" />
          )}
          {logType === "turning" && (
            <Sel value={fields.turnPosition || "Left side"} onChange={(v) => setF("turnPosition", v)} options={SELECTS.turnPosition} tid="turning-position" className="md:w-56" />
          )}
          {logType === "careclock" && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Input placeholder="Task / check" value={fields.clockTask || ""} onChange={(e) => setF("clockTask", e.target.value)} className="border-stone-300 bg-white" data-testid="careclock-task" />
              <Input type="time" value={fields.clockTime || ""} onChange={(e) => setF("clockTime", e.target.value)} className="border-stone-300 bg-white" data-testid="careclock-time" />
              <Sel value={fields.clockStatus || "Done"} onChange={(v) => setF("clockStatus", v)} options={SELECTS.clockStatus} tid="careclock-status" />
            </div>
          )}
          {logType === "meal" && (
            <div className="grid grid-cols-2 gap-3">
              <Sel value={fields.mealName || "Breakfast"} onChange={(v) => setF("mealName", v)} options={SELECTS.mealName} tid="meal-name" />
              <Sel value={fields.mealPct || "100%"} onChange={(v) => setF("mealPct", v)} options={SELECTS.mealPct} tid="meal-pct" />
            </div>
          )}
          {logType === "wandering" && (
            <div className="grid grid-cols-2 gap-3">
              <Sel value={fields.wandRedirected || "Yes"} onChange={(v) => setF("wandRedirected", v)} options={SELECTS.wandRedirected} tid="wandering-redirected" />
              <Input placeholder="Location (optional)" value={fields.wandLocation || ""} onChange={(e) => setF("wandLocation", e.target.value)} className="border-stone-300 bg-white" data-testid="wandering-location" />
            </div>
          )}
          {logType === "supervision" && (
            <Sel value={fields.supObservation || "Supervised throughout"} onChange={(v) => setF("supObservation", v)} options={SELECTS.supObservation} tid="supervision-observation" className="md:w-72" />
          )}
        </div>
        {logType !== "note" && (
          <Input placeholder="Comment (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-3 border-stone-300 bg-white" data-testid="entry-comment" />
        )}
        <div className="mt-3 flex justify-end">
          <Button onClick={add} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="add-note-button">
            {busy ? "Adding..." : "Add Entry"}
          </Button>
        </div>
      </div>

      {incidentPrompt && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-4" data-testid="fall-incident-prompt">
          <p className="text-sm text-stone-700"><span className="font-semibold text-[#C0392B]">This fall involved injury.</span> Falls like this usually belong in an incident report too.</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setIncidentOpen(true)} className="bg-[#C0392B] text-white hover:bg-stone-900" data-testid="fall-incident-open">File Incident Report</Button>
            <Button size="sm" variant="outline" onClick={() => setIncidentPrompt(null)} className="border-stone-300 text-stone-500" data-testid="fall-incident-dismiss">Dismiss</Button>
          </div>
        </div>
      )}
      <IncidentReportDialog clientId={id} externalOpen={incidentOpen} onExternalClose={() => { setIncidentOpen(false); setIncidentPrompt(null); }}
        initialCategory="Fall" initialDescription={incidentPrompt?.description || ""} onReported={load} />

      <div className="mt-6 space-y-3" data-testid="notes-list">
        {notes.length === 0 && <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">No log entries yet.</p>}
        {notes.map((n) => {
          const meta = typeMeta(n.log_type);
          const Icon = meta.icon;
          const summary = describe(n);
          return (
            <div key={n.id} className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between">
                <p className="flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-stone-400">
                  {n.shift_date || String(n.created_at).slice(0, 10)} · {n.author}
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold normal-case ${n.log_type === "fall" ? "bg-red-50 text-red-700" : (n.log_type === "intake" || n.log_type === "fluid") ? "bg-blue-50 text-blue-700" : n.log_type === "turning" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-500"}`} data-testid={`log-badge-${n.id}`}>
                    <Icon size={11} weight="fill" /> {meta.label}
                  </span>
                </p>
                <button onClick={() => remove(n.id)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`note-delete-${n.id}`}><Trash size={15} /></button>
              </div>
              {summary && <p className="mt-2 text-sm font-medium text-stone-900" data-testid={`log-summary-${n.id}`}>{summary}</p>}
              {n.note && <p className="mt-1 whitespace-pre-wrap text-sm text-stone-700">{n.note}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
